import React from 'react';
import { confirmAlert } from 'react-confirm-alert';
import ConfirmAlert from '../../FlkAPdf/ConfirmAlert';
import styles from './RequestEocAccess.module.scss';
import Button from '@app/common/components/Button';
import Note, { NoteType } from '@app/common/components/Note';
import Card, { CardShadow, CardStyles } from '@app/common/components/cards/Card';
import Icon, { Icons } from '@app/common/components/Icon';
import { useSelector } from 'react-redux';
import { getHasUserRequestedEocAccess, isUserRoleAdmin } from '@app/selectors/user';
import { useEocAccessApi } from './hooks/useEocAccessApi';

export type EocInactiveConfirmAlertParams = {
    requestAccess: () => void;
    enableAccess: () => void;
};

export const useEocInactiveAlert = () => {
    const isAdmin = useSelector(isUserRoleAdmin);

    const { requestEocAccessMutation, enableEocAccessMutation } = useEocAccessApi();

    return () => {
        if (isAdmin) {
            enableAccessConfirmAlert(enableEocAccessMutation.mutateAsync);
        } else {
            requestAccessConfirmAlert(requestEocAccessMutation.mutateAsync);
        }
    };
};

const enableAccessConfirmAlert = (enableAccess: () => Promise<void>) => {
    confirmAlert({
        customUI: ({ onClose }) => (
            <ConfirmAlert
                title="Enable Add-on"
                message={
                    <div className={styles.requestAccessMessage}>
                        <p>It looks like Exchange of Contracts has not been enabled for your agency yet...</p>
                        <p>
                            Click "Enable Exchange of Contracts" to activate the add-on and start using it — you'll only
                            be charged for what you use
                        </p>
                        <Card className={styles.pricingCard} style={CardStyles.SQUARE} shadow={CardShadow.SMALL}>
                            <Icon icon={Icons.FLK_LOGO} className={styles.pricingIcon} />
                            <div className={styles.pricingInfo}>
                                <div>
                                    <div className={styles.pricingTitle}>Exchange of Contracts</div>
                                    <div className={styles.pricingDescription}>Free until August 2025</div>
                                </div>
                            </div>
                            <div className={styles.pricingAmount}>
                                <div className={styles.price}>$0 AUD*</div>
                                <div className={styles.perUnit}>per exchange</div>
                            </div>
                        </Card>

                        <p className={styles.pricingDisclaimer}>
                            *Standard price: $20 AUD (plus GST) per contract sent
                        </p>
                    </div>
                }
                confirmText="Enable Access to Exchange of Contracts"
                cancelText="Cancel"
                onConfirm={async () => {
                    await enableAccess();
                    onClose();
                }}
                onCancel={onClose}
                onClose={onClose}
            />
        )
    });
};

const requestAccessConfirmAlert = (requestAccess: () => Promise<void>) => {
    confirmAlert({
        customUI: ({ onClose }) => (
            <ConfirmAlert
                title="Request Access"
                message={
                    <div className={styles.requestAccessMessage}>
                        <p>It looks like Exchange of Contracts has not been enabled for your agency yet...</p>
                        <p>
                            You can view and edit your draft document(s), but will not be able to send it to your
                            Recipients for signing until your Agency Admin enables this add-on.
                        </p>
                    </div>
                }
                confirmText="Request Access to Exchange of Contracts"
                cancelText="Cancel"
                onConfirm={async () => {
                    await requestAccess();
                    onClose();
                }}
                onCancel={onClose}
                onClose={onClose}
            />
        )
    });
};

export const RequestEocAccessNote: React.FC = () => {
    const isAdmin = useSelector(isUserRoleAdmin);
    const hasUserRequestedEocAccess = useSelector(getHasUserRequestedEocAccess);

    const eocInactiveConfirmAlert = useEocInactiveAlert();

    return (
        <Note
            type={NoteType.WARNING2}
            title={
                'Exchange of Contracts is currently not enabled for this agency.' +
                (!isAdmin && hasUserRequestedEocAccess ? ' Your request for access is pending.' : '')
            }
            inline
        >
            {(isAdmin || !hasUserRequestedEocAccess) && (
                <Button
                    type="button"
                    link
                    onClick={() => eocInactiveConfirmAlert()}
                    className={styles.requestAccessButton}
                >
                    {isAdmin ? 'Enable access' : 'Request access'}
                </Button>
            )}
        </Note>
    );
};
